import {FC, ReactNode, useMemo} from 'react';
import {JsFormConfigContext, TJsFormConfigContext} from '@progress-fe/ui-kit';
import {hasValue} from '@progress-fe/core';
import {observer} from 'mobx-react-lite';
import {useStore} from 'core';
import {castTrees} from 'core/utils';

const ProjectJsFormConfigContextFC: FC<{children: ReactNode}> = ({children}) => {
  const {techProcessStore, samplesAndBlendsStore, catalogsStore, favoriteComponents} = useStore();
  const {addComponentsToFavorite, removeComponentsFromFavorite, projectSettings} = techProcessStore;
  const {pureComponents, lumpComponents, componentsDictionary} = catalogsStore;
  const {sampleDictionary} = samplesAndBlendsStore;
  const {pseudoComponents} = projectSettings;
  const {tpCatalogs} = techProcessStore;

  const isTechProcess = useMemo(() => {
    return hasValue(techProcessStore.projectInfo?.type);
  }, [techProcessStore.projectInfo?.type]);

  const isInspector = useMemo(() => {
    const techProcessInspector = techProcessStore?.developerMode.isInspector;
    const samplesAndBlendsInspector = samplesAndBlendsStore?.developerMode.isInspector;
    return techProcessInspector || samplesAndBlendsInspector;
  }, [
    techProcessStore?.developerMode.isInspector,
    samplesAndBlendsStore?.developerMode.isInspector
  ]);

  const value: TJsFormConfigContext = useMemo(
    () => ({
      isInspector: isInspector,
      trees: castTrees(tpCatalogs.treeCatalog.trees),
      components: {
        favorites: favoriteComponents,
        pureList: pureComponents,
        lumpList: lumpComponents,
        pseudoList: pseudoComponents,
        dictionary: componentsDictionary
      },
      samples: sampleDictionary,
      addToFavorites: addComponentsToFavorite,
      removeFromFavorites: removeComponentsFromFavorite,
      runCallback: isTechProcess ? techProcessStore.runCallback : samplesAndBlendsStore.runCallback
    }),
    [
      isTechProcess,
      isInspector,
      techProcessStore,
      samplesAndBlendsStore,
      addComponentsToFavorite,
      componentsDictionary,
      favoriteComponents,
      pureComponents,
      lumpComponents,
      pseudoComponents,
      removeComponentsFromFavorite,
      tpCatalogs.treeCatalog.trees,
      sampleDictionary
    ]
  );

  return <JsFormConfigContext.Provider value={value}>{children}</JsFormConfigContext.Provider>;
};

export const ProjectJsFormConfigContext = observer(ProjectJsFormConfigContextFC);
